import { Bell, MessageCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import { appTopBarStyles as styles } from './AppTopBar.styles';
import { formatGreetingDate } from './greeting';

import { ROUTES } from '@/app/routes';
import { Avatar } from '@/components/atoms/Avatar';
import { cn } from '@/lib/cn';

interface AppTopBarProps {
  user: { displayName: string; avatarUrl: string | null };
  unread: { notifications: number; messages: number };
  /**
   * Titre de l'écran courant. Absent sur le tableau de bord, qui affiche à la
   * place la date et le bonjour (`meeply-home-formats`).
   */
  title?: string;
  onOpenDrawer: () => void;
  className?: string;
}

/**
 * Barre haute des formats étroits.
 *
 * Elle n'existe que là : à partir du desktop, la barre haute applicative et le
 * rail portent déjà l'avatar, les notifications et les messages. C'est aussi le
 * seul point d'entrée vers les réglages sur téléphone — d'où le tiroir ouvert
 * par l'avatar, la barre basse n'ayant que quatre places.
 */
export function AppTopBar({
  user,
  unread,
  title,
  onOpenDrawer,
  className,
}: Readonly<AppTopBarProps>) {
  const { t, i18n } = useTranslation('nav');

  return (
    <div className={cn(styles.root(), className)}>
      <button
        type="button"
        onClick={onOpenDrawer}
        aria-label={t('aria.openMenu')}
        aria-haspopup="dialog"
        className={styles.avatarButton()}
      >
        <Avatar src={user.avatarUrl} name={user.displayName} size="md" />
      </button>

      {title ? (
        <h1 className="min-w-0 flex-1 truncate text-xl font-extrabold text-fg">
          {title}
        </h1>
      ) : (
        <div className="min-w-0 flex-1">
          <p className="truncate text-2xs text-fg-subtle">
            {formatGreetingDate(new Date(), i18n.language)}
          </p>
          {/* Le bonjour EST le titre de l'écran d'accueil : sur les formats
              étroits c'est la barre haute qui porte le `h1`, jamais le contenu
              en dessous — sinon la page en annonce deux. */}
          <h1 className="truncate font-bold text-fg">
            {t('greeting', { name: user.displayName })}
          </h1>
        </div>
      )}

      <Link
        to={ROUTES.notifications}
        aria-label={
          unread.notifications > 0
            ? `${t('notifications')} — ${t('aria.unread', { count: unread.notifications })}`
            : t('notifications')
        }
        className={styles.iconButton()}
      >
        <Bell aria-hidden className="size-5" />
        {/* Un point, pas un nombre : la maquette ne chiffre que les messages. */}
        {unread.notifications > 0 && (
          <span aria-hidden className={styles.dot()} />
        )}
      </Link>

      <Link
        to={ROUTES.messages}
        aria-label={
          unread.messages > 0
            ? `${t('messages')} — ${t('aria.unread', { count: unread.messages })}`
            : t('messages')
        }
        className={styles.iconButton()}
      >
        <MessageCircle aria-hidden className="size-5" />
        {unread.messages > 0 && (
          <span aria-hidden className={styles.badge()}>
            {unread.messages}
          </span>
        )}
      </Link>
    </div>
  );
}
